(() => {
  const selectors = {
    track: '[data-kosbling-rail-track]',
    status: '[data-kosbling-rail-status]',
    retry: '[data-kosbling-rail-retry]',
    sentinel: '[data-kosbling-rail-sentinel]',
    previous: '[data-kosbling-rail-previous]',
    next: '[data-kosbling-rail-next]',
    card: '[data-product-id]'
  };
  const historyKey = 'kosblingProgressiveBrowsing';
  const historyVersion = 1;
  const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const absoluteUrl = (value) => new URL(value, window.location.href);
  const hasSortParameter = (value) => {
    try {
      const source = String(value || '');
      if (!source) return false;
      const parameters = source.includes('?') || source.startsWith('/') || /^[a-z][a-z\d+.-]*:/i.test(source)
        ? absoluteUrl(source).searchParams
        : new URLSearchParams(source.replace(/^\?/, ''));
      return parameters.has('sort_by');
    } catch (error) { return true; }
  };
  const compareExactStrings = (left, right) => left === right ? 0 : (left < right ? -1 : 1);
  const queryIdentity = (value) => {
    try {
      const source = String(value || '');
      const query = source.includes('?') ? absoluteUrl(source).search : source;
      const parameters = new URLSearchParams(query.replace(/^\?/, ''));
      parameters.delete('page');
      parameters.delete('section_id');
      parameters.delete('sort_by');
      const entries = [...parameters.entries()];
      entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => {
        const keyOrder = compareExactStrings(leftKey, rightKey);
        return keyOrder || compareExactStrings(leftValue, rightValue);
      });
      return JSON.stringify(entries);
    } catch (error) { return null; }
  };
  const pageNumber = (value) => {
    const source = String(value ?? '');
    if (!/^[1-9]\d*$/.test(source)) return null;
    const page = Number(source);
    return Number.isSafeInteger(page) ? page : null;
  };
  const pageFromUrl = (value) => {
    if (!value || hasSortParameter(value)) return null;
    const pages = absoluteUrl(value).searchParams.getAll('page');
    return pages.length === 1 ? pageNumber(pages[0]) : null;
  };
  const historyNamespace = (state) => {
    if (state !== null && state !== undefined && !isRecord(state)) throw new Error('History state must be an object.');
    const base = state || {};
    const existing = base[historyKey];
    if (existing !== undefined && (!isRecord(existing)
      || existing.version !== historyVersion
      || (existing.rails !== undefined && !isRecord(existing.rails))
      || (existing.collections !== undefined && !isRecord(existing.collections)))) {
      throw new Error('Progressive browsing history is malformed.');
    }
    return { state: base, namespace: existing || { version: historyVersion, rails: {}, collections: {} } };
  };

  class ProductRail {
    constructor(root, dependencies = {}) {
      this.root = root;
      this.track = root.querySelector(selectors.track);
      this.status = root.querySelector(selectors.status);
      this.retry = root.querySelector(selectors.retry);
      this.fetch = dependencies.fetch || window.fetch?.bind(window);
      this.Observer = dependencies.IntersectionObserver || window.IntersectionObserver;
      this.inFlight = false;
      this.disposed = false;
      this.restoring = false;
      this.historyEnabled = true;
      this.initialNextUrl = root.dataset.nextUrl || '';
      this.initialPage = pageNumber(root.dataset.page);
      this.currentPage = this.initialPage;
      this.loadedPages = [];
      this.appendedCards = [];
      this.initialProductIds = new Set([...this.track.querySelectorAll(selectors.card)].map((card) => card.dataset.productId));
      this.productIds = new Set(this.initialProductIds);
      this.queryIdentity = Object.hasOwn(root.dataset, 'queryString') && !hasSortParameter(root.dataset.queryString)
        ? queryIdentity(root.dataset.queryString)
        : null;
      this.collectionUrl = this.validCollectionUrl(root.dataset.collectionUrl);
      this.paginationPath = this.validPaginationPath(root.dataset.paginationPath);
      this.stateId = this.identityKey();
      this.onKeydown = this.onKeydown.bind(this);
      this.onRetry = this.load.bind(this);
      this.onPrevious = () => this.scroll(-1);
      this.onNext = () => this.scroll(1);
      this.onScroll = this.onScroll.bind(this);
      this.onPageHide = this.persistPosition.bind(this);

      let savedEntry = null;
      try {
        if (!this.stateId) throw new Error('Rail identity is incomplete.');
        savedEntry = this.readHistoryEntry();
        if (!savedEntry) this.writeHistoryEntry();
      } catch (error) {
        this.historyEnabled = false;
      }

      this.root.addEventListener?.('keydown', this.onKeydown);
      this.retry?.addEventListener('click', this.onRetry);
      this.root.querySelector(selectors.previous)?.addEventListener('click', this.onPrevious);
      this.root.querySelector(selectors.next)?.addEventListener('click', this.onNext);
      this.track.addEventListener?.('scroll', this.onScroll, { passive: true });
      window.addEventListener?.('pagehide', this.onPageHide);
      const sentinel = root.querySelector(selectors.sentinel);
      if (this.Observer && sentinel) {
        this.observer = new this.Observer((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        }, { root: this.track, rootMargin: '0px 240px 0px 0px' });
        this.observer.observe(sentinel);
      }
      this.updateControls();
      if (savedEntry && this.historyEnabled) {
        this.restoring = true;
        this.restorePromise = this.restore(savedEntry);
      } else this.restorePromise = Promise.resolve(false);
    }

    validCollectionUrl(value) {
      try {
        const url = absoluteUrl(value);
        return url.origin === window.location.origin ? url : null;
      } catch (error) { return null; }
    }

    validPaginationPath(value) {
      try {
        const source = String(value || '');
        if (!source.startsWith('/')) return null;
        const url = absoluteUrl(source);
        if (url.origin !== window.location.origin || url.search || url.hash) return null;
        return url.pathname;
      } catch (error) { return null; }
    }

    identityKey() {
      if (!this.root.dataset.sectionId || !this.root.dataset.collectionHandle
        || !this.collectionUrl || !this.paginationPath || !this.queryIdentity || !this.initialPage) return '';
      return JSON.stringify(['rail', this.root.dataset.sectionId, this.root.dataset.collectionHandle,
        this.collectionUrl.pathname, this.paginationPath, this.queryIdentity]);
    }

    validateHistoryEntry(entry) {
      if (!isRecord(entry) || entry.initialPage !== this.initialPage
        || !Array.isArray(entry.loadedPages) || !Number.isFinite(entry.position) || entry.position < 0) {
        throw new Error('Rail history entry is malformed.');
      }
      let expected = this.initialPage + 1;
      for (const page of entry.loadedPages) {
        if (!Number.isSafeInteger(page) || page !== expected) throw new Error('Rail history pages are not sequential.');
        expected += 1;
      }
      return { loadedPages: [...entry.loadedPages], position: entry.position };
    }

    readHistoryEntry() {
      const { namespace } = historyNamespace(window.history.state);
      const entry = (namespace.rails || {})[this.stateId];
      return entry === undefined ? null : this.validateHistoryEntry(entry);
    }

    writeHistoryEntry() {
      if (!this.historyEnabled) return false;
      const { state, namespace } = historyNamespace(window.history.state);
      const nextNamespace = {
        ...namespace,
        version: historyVersion,
        rails: {
          ...(namespace.rails || {}),
          [this.stateId]: {
            initialPage: this.initialPage,
            loadedPages: [...this.loadedPages],
            position: Math.max(0, Number(this.track.scrollLeft) || 0),
          },
        },
        collections: { ...(namespace.collections || {}) },
      };
      window.history.replaceState({ ...state, [historyKey]: nextNamespace }, '', window.location.href);
      return true;
    }

    persistPosition() {
      if (!this.historyEnabled || this.restoring || this.disposed) return;
      try { this.writeHistoryEntry(); } catch (error) { this.rollbackToInitial(true); }
    }

    onScroll() {
      if (this.scrollFrame) return;
      const schedule = window.requestAnimationFrame || ((callback) => callback());
      this.scrollFrame = schedule(() => {
        this.scrollFrame = null;
        this.updateControls();
        this.persistPosition();
      });
    }

    get nextUrl() { return this.root.dataset.nextUrl || ''; }

    setStatus(message) { if (this.status) this.status.textContent = message; }

    showRetry() { if (this.retry) this.retry.hidden = false; }

    hideRetry() { if (this.retry) this.retry.hidden = true; }

    scroll(direction) {
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.track.scrollBy?.({ left: direction * Math.max(this.track.clientWidth * 0.8, 240), behavior });
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.scroll(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.scroll(1); }
    }

    updateControls() {
      const allFit = !this.nextUrl && this.track.scrollWidth <= this.track.clientWidth + 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = !this.nextUrl && this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
      const previous = this.root.querySelector(selectors.previous);
      const next = this.root.querySelector(selectors.next);
      if (previous) { previous.hidden = allFit; previous.disabled = atStart; }
      if (next) { next.hidden = allFit; next.disabled = atEnd || this.inFlight; }
      if (!this.nextUrl && atEnd) this.setStatus('All products are loaded.');
    }

    isNearEnd() { return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 240; }

    appendCards(cards) {
      const sentinel = this.track.querySelector?.(selectors.sentinel) || this.root.querySelector(selectors.sentinel);
      if (sentinel && this.track.insertBefore) cards.forEach((card) => this.track.insertBefore(card, sentinel));
      else this.track.append(...cards);
    }

    requestIdentity(requestUrl, expectedPage = this.currentPage + 1) {
      try {
        if (hasSortParameter(requestUrl)) return null;
        const requested = absoluteUrl(requestUrl);
        const page = pageFromUrl(requestUrl);
        if (requested.origin !== window.location.origin || requested.pathname !== this.paginationPath
          || page !== expectedPage || queryIdentity(requestUrl) !== this.queryIdentity) return null;
        return { page, url: requested };
      } catch (error) { return null; }
    }

    readBatch(sourceRoot, requestUrl) {
      const section = sourceRoot.dataset.sectionId;
      const collection = sourceRoot.dataset.collectionHandle;
      if (section !== this.root.dataset.sectionId || collection !== this.root.dataset.collectionHandle) return null;
      if (!Object.hasOwn(sourceRoot.dataset, 'queryString')
        || !Object.hasOwn(sourceRoot.dataset, 'page')
        || !Object.hasOwn(sourceRoot.dataset, 'nextUrl')
        || hasSortParameter(sourceRoot.dataset.queryString)
        || hasSortParameter(sourceRoot.dataset.nextUrl)) return null;
      const request = this.requestIdentity(requestUrl);
      const responsePage = pageNumber(sourceRoot.dataset.page);
      const responseCollectionUrl = this.validCollectionUrl(sourceRoot.dataset.collectionUrl);
      const responsePaginationPath = this.validPaginationPath(sourceRoot.dataset.paginationPath);
      if (!request || responsePage !== request.page || !responseCollectionUrl
        || responseCollectionUrl.pathname !== this.collectionUrl.pathname
        || responsePaginationPath !== this.paginationPath
        || queryIdentity(sourceRoot.dataset.queryString) !== this.queryIdentity) return null;
      const nextUrl = sourceRoot.dataset.nextUrl || '';
      if (nextUrl && !this.requestIdentity(nextUrl, responsePage + 1)) return null;
      const sourceTrack = sourceRoot.querySelector(selectors.track);
      if (!sourceTrack) return null;
      const batchIds = new Set();
      const cards = [...sourceTrack.querySelectorAll(selectors.card)].filter((card) => {
        const id = card.dataset.productId;
        if (!id || this.productIds.has(id)) return false;
        if (batchIds.has(id)) return false;
        batchIds.add(id);
        return true;
      });
      return { cards, page: responsePage, nextUrl };
    }

    commitBatch(batch) {
      this.appendCards(batch.cards);
      batch.cards.forEach((card) => {
        const id = card.dataset.productId;
        this.productIds.add(id);
      });
      this.appendedCards.push(...batch.cards);
      this.currentPage = batch.page;
      this.loadedPages.push(batch.page);
      this.root.dataset.page = String(batch.page);
      this.root.dataset.nextUrl = batch.nextUrl;
    }

    rollbackToInitial(disableHistory = false) {
      this.appendedCards.forEach((card) => card.remove?.());
      this.appendedCards = [];
      this.productIds = new Set(this.initialProductIds);
      this.loadedPages = [];
      this.currentPage = this.initialPage;
      this.root.dataset.page = String(this.initialPage || '');
      this.root.dataset.nextUrl = this.initialNextUrl;
      this.track.scrollLeft = 0;
      if (disableHistory) this.historyEnabled = false;
      this.showRetry();
      this.setStatus('Could not restore more products. Try again.');
      this.updateControls();
      if (this.historyEnabled) {
        try { this.writeHistoryEntry(); } catch (error) { this.historyEnabled = false; }
      }
    }

    async restore(entry) {
      try {
        for (const page of entry.loadedPages) {
          if (pageFromUrl(this.nextUrl) !== page || !await this.load({ replay: true })) {
            throw new Error('Rail replay drifted from its recorded page boundary.');
          }
        }
        this.track.scrollLeft = entry.position;
        this.updateControls();
        return true;
      } catch (error) {
        this.rollbackToInitial(false);
        return false;
      } finally { this.restoring = false; }
    }

    async load(options = {}) {
      if (this.inFlight || this.disposed || !this.nextUrl || !this.fetch) return false;
      const replay = options?.replay === true;
      if (!this.historyEnabled || (this.restoring && !replay)) return false;
      const requestUrl = this.nextUrl;
      if (!this.requestIdentity(requestUrl)) {
        this.showRetry();
        this.setStatus('Could not load more products. Try again.');
        return false;
      }
      this.inFlight = true;
      this.root.setAttribute('aria-busy', 'true');
      this.hideRetry();
      this.setStatus('Loading more products.');
      this.updateControls();
      let continueLoading = false;
      try {
        const response = await this.fetch(requestUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Rail request failed: ${response.status}`);
        const markup = await response.text();
        const document = new DOMParser().parseFromString(markup, 'text/html');
        const sourceRoot = document.querySelector(`kosbling-product-rail[data-section-id="${this.root.dataset.sectionId}"]`);
        const batch = sourceRoot && this.readBatch(sourceRoot, requestUrl);
        if (!batch) throw new Error('Rail response did not match the exact requested page identity.');
        if (this.disposed || this.root.isConnected === false) return false;
        this.commitBatch(batch);
        if (!replay) {
          try { this.writeHistoryEntry(); } catch (error) {
            this.rollbackToInitial(true);
            throw error;
          }
          continueLoading = Boolean(batch.cards.length && this.nextUrl && this.isNearEnd());
        }
        this.setStatus(this.nextUrl ? `${batch.cards.length} more products loaded.` : 'All products are loaded.');
        return true;
      } catch (error) {
        if (!this.disposed) {
          this.showRetry();
          this.setStatus('Could not load more products. Try again.');
        }
        return false;
      } finally {
        this.inFlight = false;
        this.root.removeAttribute('aria-busy');
        this.updateControls();
        if (continueLoading && !this.disposed) this.load();
      }
    }

    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.observer?.disconnect();
      this.root.removeEventListener?.('keydown', this.onKeydown);
      this.track.removeEventListener?.('scroll', this.onScroll);
      window.removeEventListener?.('pagehide', this.onPageHide);
      this.retry?.removeEventListener?.('click', this.onRetry);
      this.root.querySelector(selectors.previous)?.removeEventListener?.('click', this.onPrevious);
      this.root.querySelector(selectors.next)?.removeEventListener?.('click', this.onNext);
    }
  }

  const controllers = new WeakMap();
  const mount = (root) => {
    if (root && !controllers.has(root)) controllers.set(root, new ProductRail(root));
    return controllers.get(root);
  };
  const dispose = (root) => controllers.get(root)?.dispose();
  const mountAll = (scope = document) => scope.querySelectorAll?.('kosbling-product-rail').forEach(mount);

  window.KosblingProductRail = { ProductRail, mount, dispose, mountAll, queryIdentity, hasSortParameter };
  document.addEventListener('DOMContentLoaded', () => mountAll());
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll?.('kosbling-product-rail').forEach(dispose));
})();
